import { posthog } from '@/lib/posthog';
import { isStaleChunkError, reloadForStaleChunk } from '@/lib/stale-chunk';
import { useEffect } from 'react';
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router';
import { ErrorScreen } from './ErrorScreen';

/**
 * Catches errors thrown during rendering, in loaders/actions, and 404 responses.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();

  const isResponse = isRouteErrorResponse(error);
  const isNotFound = isResponse && error.status === 404;
  const isChunk = !isResponse && isStaleChunkError(error);

  useEffect(() => {
    if (isNotFound) return;

    // usually after a deploy
    if (isChunk && reloadForStaleChunk()) return;
    if (isChunk) return; // expected on deploy — don't report as an exception
    posthog.captureException(error instanceof Error ? error : new Error(String(error)), {
      source: 'route_error_boundary',
      status: isResponse ? error.status : undefined,
    });
  }, [error, isResponse, isNotFound, isChunk]);

  if (isChunk) {
    return (
      <ErrorScreen
        code="UPDATE"
        title="A new version is available"
        description="The app was updated while this page was open. Reload to get the latest version."
        onReload={() => window.location.reload()}
        onHome={() => navigate('/')}
      />
    );
  }

  if (isNotFound) {
    return (
      <ErrorScreen
        code="404"
        title="Page not found"
        description="The page you're looking for doesn't exist or may have moved."
        onHome={() => navigate('/')}
      />
    );
  }

  return (
    <ErrorScreen
      code={isResponse ? String(error.status) : 'ERROR'}
      title="Something went wrong"
      description="We hit an unexpected error loading this page. You can try again, or head back to your dashboard."
      onRetry={() => navigate(0)}
      onHome={() => navigate('/')}
    />
  );
}
